import { IconPen, IconPlusRounded, IconTrash } from "@aksara-ui/icons";
import {
  Box,
  Card,
  Table,
  TableHead,
  TableBody,
  TableHeadCell,
  TableBodyCell,
  TableHeadRow,
  TableBodyRow,
  IconButton,
  theme,
  Button,
  Text,
  Stack,
  Badge,
  Avatar,
} from "@aksara-ui/react";

import React, { useState, useEffect } from "react";
import styled from "styled-components";

import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import {
  getAllUsers,
  setModalConfirmationOpen,
  setModalFormOpen,
} from "redux/actions";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import { UserConfirmationModal, UserFormModal } from "./components";
import { toCapitilizeCase } from "utilities/helper/toCapitalizeCase";
import {
  Pagination,
  PaginationDetail,
  PaginationFilter,
} from "components/Pagination";
import { InputSearchbox } from "components/Form/InputSearchbox";
import { stringToColor } from "utilities/helper/stringToColor";

const HeaderWrapper = styled(Box)`
  justify-content: space-between;
  margin-bottom: 30px;
  gap: 20px;
`;

const UsersContent: React.FC = () => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 576 });

  const { isLoading, isModalFormOpen, isModalConfirmationOpen } = useSelector(
    (state: any) => state.globalReducer
  );
  const { users } = useSelector((state: any) => state.userReducer);
  const { currentPage, totalPage, limitPage, lengthPage } = useSelector(
    (state: any) => state.userReducer
  );

  const [selectedUserId, setSelectedUserId] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
    dispatch(getAllUsers(1, event.target.value, limitPage) as any);
  };

  const handleClearSearchChange = () => {
    setSearchValue("");
    dispatch(getAllUsers(currentPage, "", limitPage) as any);
  };

  const handleOpenFormModal = (userId: string = "") => {
    dispatch(setModalFormOpen(true) as any);
    setSelectedUserId(userId);
  };

  const handleCloseFormModal = () => {
    dispatch(setModalFormOpen(false) as any);
    setSelectedUserId("");
  };

  const handleOpenConfirmModal = (userId: string) => {
    dispatch(setModalConfirmationOpen(true) as any);
    setSelectedUserId(userId);
  };

  const handleCloseConfirmModal = () => {
    dispatch(setModalConfirmationOpen(false) as any);
    setSelectedUserId("");
  };

  useEffect(() => {
    dispatch(getAllUsers() as any);
  }, [dispatch]);

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      <HeaderWrapper
        display={"flex"}
        flexDirection={"column"}
        style={{ gap: 20 }}
      >
        <Box display={"flex"}>
          <Text scale={[500, 600]} fontWeight={700}>
            User Management
          </Text>
        </Box>

        <Box
          display={"flex"}
          justifyContent={"space-between"}
          style={{ gap: isMobile ? 10 : 20 }}
        >
          <InputSearchbox
            autoComplete="off"
            name="textDummy"
            placeholder="Search..."
            size={32}
            onChange={handleSearchChange}
            onClearButton={handleClearSearchChange}
          />

          <Stack display={"flex"} style={{ gap: 10 }}>
            <Button
              variant={"primary"}
              borderRadius={10}
              style={{ fontWeight: 600 }}
              icon={IconPlusRounded}
              iconPosition={"left"}
              size={32}
              onClick={() => handleOpenFormModal()}
            >
              Add User
            </Button>
          </Stack>
        </Box>
      </HeaderWrapper>

      <Card maxHeight={"100%"} overflow={"auto"} elevation={2}>
        <Table>
          <TableHead
            position={"sticky"}
            top={0}
            backgroundColor={theme.colors.grey02}
          >
            <TableHeadRow>
              <TableHeadCell>Name</TableHeadCell>
              <TableHeadCell>Email</TableHeadCell>
              <TableHeadCell>Phone Number</TableHeadCell>
              <TableHeadCell>Role</TableHeadCell>
              <TableHeadCell>Birthdate</TableHeadCell>
              <TableHeadCell>Greeting</TableHeadCell>
              {/* <TableHeadCell>Address</TableHeadCell> */}
              <TableHeadCell>Phone Verified</TableHeadCell>
              <TableHeadCell>Email Verified</TableHeadCell>
              <TableHeadCell>Action</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodyRowsSkeleton rowsNumber={10} columnsNumber={9} />
            ) : !users.length ? (
              <TableBodyRow>
                <TableBodyCell colSpan={9} textAlign={"center"}>
                  No data available in table
                </TableBodyCell>
              </TableBodyRow>
            ) : (
              users.map((user: any) => {
                const name = user.name;
                const color = stringToColor(name);

                return (
                  <TableBodyRow key={user.id}>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      <Avatar
                        name={name}
                        backgroundColor={color}
                        style={{ marginRight: 15 }}
                      />
                      {name}
                    </TableBodyCell>
                    <TableBodyCell>{user.email}</TableBodyCell>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      +62 {user.phone_number.slice(2)}
                    </TableBodyCell>
                    <TableBodyCell>
                      <Badge size="md" variant="info">
                        {toCapitilizeCase(user.role)}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>+62 {user.phone_number.slice(2)}</TableBodyCell>
                    <TableBodyCell>
                      <Badge size="md">{toCapitilizeCase(user.greeting)}</Badge>{" "}
                    </TableBodyCell>
                    {/* <TableBodyCell>{user.address}</TableBodyCell> */}
                    <TableBodyCell>
                      <Badge
                        size="md"
                        variant={
                          user.phone_is_verified !== 1 ? "critical" : "success"
                        }
                      >
                        {user.phone_is_verified !== 1 ? "No" : "Yes"}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>
                      <Badge
                        size="md"
                        variant={
                          user.email_is_verified !== 1 ? "critical" : "success"
                        }
                      >
                        {user.email_is_verified !== 1 ? "No" : "Yes"}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>
                      <IconButton onClick={() => handleOpenFormModal(user.id)}>
                        <IconPen fill={theme.colors.green06} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenConfirmModal(user.id)}
                      >
                        <IconTrash fill={theme.colors.red06} />
                      </IconButton>
                    </TableBodyCell>
                  </TableBodyRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        paddingY={10}
      >
        {!isMobile ? (
          <PaginationDetail
            page={currentPage}
            limit={limitPage}
            length={lengthPage}
          />
        ) : (
          ""
        )}
        <Pagination
          current={currentPage}
          total={totalPage}
          onSelect={(select: any) =>
            dispatch(getAllUsers(select, searchValue, limitPage) as any)
          }
        />
        <PaginationFilter
          selectedItem={limit}
          onChange={(selectedItem) => {
            if (selectedItem) {
              setLimit(selectedItem);
              dispatch(getAllUsers(1, searchValue, selectedItem) as any);
            }
          }}
          items={[10, 20, 30, 40, 50]}
        />
      </Box>

      <UserFormModal
        userId={selectedUserId}
        isOpen={isModalFormOpen}
        onClose={() => handleCloseFormModal()}
      />

      <UserConfirmationModal
        userId={selectedUserId}
        isOpen={isModalConfirmationOpen}
        onClose={() => handleCloseConfirmModal()}
      />
    </Box>
  );
};

export default UsersContent;
